/**
 * Item text heuristics — shared, dependency-free.
 *
 * One copy for both runtimes: the app (src/lib/semanticCollisions.ts and
 * src/lib/goodItemTest.ts re-export from here) and the edge functions (the
 * ambient agent runtime: the estimator's clarity pre-check and precedent
 * ranking, the Clarity Grader).
 */

/** Word-set overlap of two strings, 0–1. */
export function jaccardSimilarity(str1: string, str2: string): number {
  const set1 = new Set(str1.toLowerCase().split(/\s+/));
  const set2 = new Set(str2.toLowerCase().split(/\s+/));

  const intersection = new Set([...set1].filter((x) => set2.has(x)));
  const union = new Set([...set1, ...set2]);

  return union.size > 0 ? intersection.size / union.size : 0;
}

/**
 * Clarity of a title and description, 0–100 each, by the same heuristics the
 * good-item test applies. Pure; used as the Value Estimator's pre-check so a
 * thin item abstains before any model call (PRD-value-suggestion-agent.md §4.1),
 * and as the Clarity Grader's grade (SPEC-custom-agent-template.md §7).
 */
export function scoreItemClarity(title: string, description: string): {
  titleClarity: number;
  descriptionClarity: number;
  overall: number;
  suggestions: string[];
} {
  const titleLength = title.length;
  const titleHasVerb = /\b(add|create|build|fix|enable|improve|update|implement|launch|develop)\b/i.test(title);
  const titleIsQuestion = title.includes('?');
  const titleHasAcronyms = /\b[A-Z]{2,}\b/.test(title);

  let titleClarity = 100;
  if (titleLength < 10) titleClarity -= 30;
  if (titleLength > 100) titleClarity -= 20;
  if (!titleHasVerb) titleClarity -= 20;
  if (titleIsQuestion) titleClarity -= 30;
  if (titleHasAcronyms) titleClarity -= 10; // Acronyms may need explanation

  let descriptionClarity = 100;
  if (description.length < 20) descriptionClarity -= 40;
  if (description.length > 1000) descriptionClarity -= 10; // Too verbose
  if (!description.includes(' ')) descriptionClarity -= 50; // No real description

  const suggestions: string[] = [];
  if (!titleHasVerb) suggestions.push('Add an action verb to the title');
  if (titleLength < 10) suggestions.push('Make the title more descriptive');
  if (description.length < 20) suggestions.push('Add a more detailed description');

  return {
    titleClarity,
    descriptionClarity,
    overall: (titleClarity + descriptionClarity) / 2,
    suggestions,
  };
}
